import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { styled } from "@mui/material/styles";
import { ethers } from "ethers";
import { useDexProvider } from "../../../Hooks/DexProvider";
import { TOKENS } from "../../../Components/config";
import {
  SwapQuoteComponentProps,
  SwapQuoteComponentRef,
} from "../../../Components/interface";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import { Typography } from "@mui/material";
import { StepContent } from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";

interface Coin {
  symbol: string;
  poolBalance: number;
  decimals: number;
}

interface Pool {
  coins: Coin[];
  address: string;
}

interface RateEdge {
  rate: number;
}

interface Graph {
  [coin: string]: {
    [neighbor: string]: {
      rate: number;
      poolAddress: string;
    };
  };
}

export const CurveQuoteComponent = forwardRef<
  SwapQuoteComponentRef,
  SwapQuoteComponentProps
>(({ tokenIn, tokenOut, amountIn, fee, OnComplete }, ref) => {
  const [pools, setPools] = useState<Pool[]>([]);
  const [paths, setPaths] = useState<string[]>([]);
  const [poolFees, setPoolFees] = useState<string[]>([]);
  const [quote, setQuote] = useState<string | null>(null);
  const [poolfee, setPoolfee] = useState(fee);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ rate: number; path: string[] } | null>(
    null,
  );
  const {
    provider,
    error: errorProvider,
    isLoading: providerLoading,
  } = useDexProvider();
  const poolAbi = ["function fee() view returns (uint256)"];
  const buildGraph = (pools: Pool[]): Graph => {
    const graph: Graph = {};

    pools.forEach((pool) => {
      const { coins, address } = pool;
      for (let i = 0; i < coins.length; i++) {
        for (let j = i + 1; j < coins.length; j++) {
          const coinA = coins[i];
          const coinB = coins[j];

          if (coinA && coinB) {
            const rateAtoB =
              (coinB.poolBalance * Math.pow(10, coinA.decimals)) /
              (coinA.poolBalance * Math.pow(10, coinB.decimals));
            const rateBtoA = 1 / rateAtoB;

            graph[coinA.symbol] = graph[coinA.symbol] || {};
            graph[coinB.symbol] = graph[coinB.symbol] || {};

            graph[coinA.symbol][coinB.symbol] = {
              rate: rateAtoB,
              poolAddress: address,
            };
            graph[coinB.symbol][coinA.symbol] = {
              rate: rateBtoA,
              poolAddress: address,
            };
          }
        }
      }
    });

    return graph;
  };

  const dijkstra = (
    graph: Graph,
    start: string,
    end: string,
  ): { path: string[]; rate: number; poolAddresses: string[] } => {
    const rates: { [key: string]: number } = {};
    const previous: {
      [key: string]: { coin: string; poolAddress: string } | null;
    } = {};
    const visited: Set<string> = new Set();
    const queue: string[] = [];

    for (const node in graph) {
      rates[node] = node === start ? 1 : 0;
      previous[node] = null;
    }

    queue.push(start);

    while (queue.length > 0) {
      const current = queue.reduce((a, b) => (rates[a] > rates[b] ? a : b));
      queue.splice(queue.indexOf(current), 1);
      visited.add(current);

      if (current === end) break;

      for (const neighbor in graph[current]) {
        if (!visited.has(neighbor)) {
          const { rate, poolAddress } = graph[current][neighbor];
          const newRate = rates[current] * rate;
          if (newRate > rates[neighbor]) {
            rates[neighbor] = newRate;
            previous[neighbor] = { coin: current, poolAddress };
            queue.push(neighbor);
          }
        }
      }
    }

    const path: string[] = [];
    const poolAddresses: string[] = [];
    let currentNode: string | null = end;

    while (currentNode && previous[currentNode]) {
      path.unshift(currentNode);
      const prev: any = previous[currentNode]; // Store the non-null object temporarily
      if (prev) {
        poolAddresses.unshift(prev.poolAddress);
        currentNode = prev.coin;
      }
    }
    if (currentNode) path.unshift(currentNode); // Add start node

    return { path, rate: rates[end], poolAddresses };
  };

  const fetchPoolFee = async (poolAddress: string[]) => {
    const updatedFees = [];
    for (const address of poolAddress) {
      const fee = await fetchFee(address);
      updatedFees.push(fee);
      setPoolFees([...updatedFees]);
    }
  };

  const fetchFee = async (address: string) => {
    const poolContract = new ethers.Contract(address, poolAbi, provider);
    const feeValue = await poolContract.fee();
    return (Number(feeValue) / 1e8).toFixed(2) + "%";
  };

  const fetchQuote = async (): Promise<void> => {
    if (!TOKENS[tokenIn] || !TOKENS[tokenOut]) {
      setError("Invalid token name.");
      return;
    }

    try {
      const response = await fetch(
        "https://api.curve.fi/api/getPools/ethereum",
      );
      const data = await response.json();
      const poolData: Pool[] = data.data.poolData.map((pool: any) => ({
        id: pool.id,
        address: pool.address,
        coins: pool.coins.map((coin: any) => ({
          symbol: coin.symbol,
          poolBalance: parseFloat(coin.poolBalance || "0"),
          decimals: coin.decimals,
        })),
      }));
      setPools(poolData);
      const graph = buildGraph(poolData);
      if (graph) {
        const result = dijkstra(
          graph,
          TOKENS[tokenIn].name,
          TOKENS[tokenOut].name,
        );
        setResult(result);
        if (result.rate) {
          setQuote((amountIn * result.rate)?.toFixed(4));
        }

        if (result.path && result.path.length > 0) {
          setPaths(result.path);
        }
        if (result.poolAddresses && result.poolAddresses.length > 0) {
          fetchPoolFee(result.poolAddresses);
        }
      }
      setError(null);
      OnComplete();
    } catch (err: any) {
      setError(`Failed to fetch quote: ${err.message}`);
      setQuote(null);
    }
  };

  const QontoStepIconRoot = styled("div")<{ ownerState: { active?: boolean } }>(
    ({ theme }) => ({
      color: "#eaeaf0",
      display: "flex",
      height: 22,
      alignItems: "center",
      "& .QontoStepIcon-completedIcon": {
        color: "#219ebc",
        zIndex: 1,
        fontSize: 18,
      },
      "& .QontoStepIcon-circle": {
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "currentColor",
      },
      ...theme.applyStyles("dark", {
        color: "#219ebc",
      }),
      variants: [
        {
          props: ({ ownerState }) => ownerState.active,
          style: {
            color: "#219ebc",
          },
        },
      ],
    }),
  );

  function QontoStepIcon(props: StepIconProps) {
    const { active, completed, className } = props;

    return (
      <QontoStepIconRoot ownerState={{ active }} className={className}>
        <div className="QontoStepIcon-circle" />
      </QontoStepIconRoot>
    );
  }

  useImperativeHandle(ref, () => ({
    fetchQuote,
  }));

  return (
    <>
      {quote && (
        <div>
          <div className="flex justify-between items-center">
            <div className="creator">
              <div className="wrapper">
                <img
                  src={"/images/exchange/curve.png"}
                  alt={"Curve Finance "}
                />
              </div>
              <p>
                <ins>Curve</ins>
              </p>
            </div>
            <p>{quote}</p>
          </div>
          <Stepper
            className="curve-stepper"
            activeStep={paths.length}
            alternativeLabel
          >
            {paths.map((label, index) => (
              <Step key={label}>
                <Typography className="swap-curve-fee">
                  {poolFees[index] ? poolFees[index] : " "}
                </Typography>
                <StepLabel
                  className={
                    paths.length === index + 1
                      ? "swap-curve-label mt-[12px]"
                      : "swap-curve-label"
                  }
                  StepIconComponent={QontoStepIcon}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      )}
      {error && (
        <div className="mt-4 text-red-500">
          Failed to load data from the curve server.
        </div>
      )}
    </>
  );
});
